import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { horseService } from "../../api/horses.service";
import FamilyModal from "../NewPremium/FamilyModal";
import DisabledInputWithRemove from "../NewPremium/DisabledInputWithRemove";
import { horseListService } from "../../api/horseList.service";
import AddHorseModal from "../AddHorseModal";
import { renderNoHorsesFoundMessage, addHorseAlerts } from "../../api/helpers";
import SourAlert from "../elements/SourAlert";
import useSourAlert from "../../hooks/useSourAlert";

const Step2 = ({
	handleReportClick,
	setPedigreeData,
	pedigreeData,
	initialSelectedHorses = [],
}) => {
	const [horseInputValue, setHorseInputValue] = useState("");
	const [horseOptions, setHorseOptions] = useState([]);
	const [horseEmptyMessage, setHorseEmptyMessage] = useState(
		"Type at least 3 characters"
	);
	const [selectedHorses, setSelectedHorses] = useState(initialSelectedHorses);

	const [familyModalIsOpen, setFamilyModalIsOpen] = useState(false);
	const [selectedHorseForFamily, setSelectedHorseForFamily] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [isAddHorseModalOpen, setIsAddHorseModalOpen] = useState(false);
	const [alertProps, showAlert] = useSourAlert();

	const handleAddHorseResult = (result) => {
		if (result.type === 'success') {
			showAlert(addHorseAlerts.success);
		} else {
			showAlert(addHorseAlerts.error(result.message));
		}
	};

	const [fileName, setFileName] = useState(""); // ← estado para nombre
	const fileInputRef = useRef(null);
	const onUploadCSV = () => fileInputRef.current.click();

	const onFileChange = (e) => {
		const file = e.target.files[0];
		if (file && file.type === "text/csv") {
			setFileName(file.name);
			setPedigreeData((prev) => ({
				...prev,
				file: file,
			}));
		} else {
			alert("Please select a valid .csv file");
		}
	};

	const onDownloadTemplate = async () => {
		try {
			const blob = await horseListService.csvExample();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "csv-example.csv";
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Error downloading template:", err);
			alert("Error downloading template. Please try again.");
		}
	};

	useEffect(() => {
		if (initialSelectedHorses.length > 0) {
			setSelectedHorses(initialSelectedHorses);
		}
	}, [initialSelectedHorses]);

	useEffect(() => {
		const ids = selectedHorses.map((h) => h.value);
		setPedigreeData((prev) => ({
			...prev,
			horses: ids,
		}));
	}, [selectedHorses, setPedigreeData]);

	const showError = (msg) => {
		setErrorMessage(msg);
		setTimeout(() => setErrorMessage(""), 3000);
	};

	const handleHorseSelect = (option) => {
		if (
			option &&
			selectedHorses.some((item) => item.value === option.value)
		) {
			showError("Not accepted duplicated horses");
			return;
		}
		if (option) {
			setSelectedHorses((prev) => [...prev, option]);
			setHorseInputValue("");
			setHorseOptions([]);
		}
	};

	const removeHorse = (value) => {
		setSelectedHorses((prev) =>
			prev.filter((item) => item.value !== value)
		);
	};

	const openFamilyModal = (horse) => {
		setSelectedHorseForFamily(horse);
		setFamilyModalIsOpen(true);
	};

	useEffect(() => {
		if (horseInputValue.trim() === "") {
			setHorseOptions([]);
			return;
		}
		setHorseEmptyMessage(
			horseInputValue.length >= 3
				? "Searching for horses..."
				: "Type at least 3 characters"
		);
		const delay = setTimeout(() => {
			if (horseInputValue.length < 3) return;
			horseService
				.find({ keyword: horseInputValue })
				.then((res) => {
					if (res && res.length > 0) {
						const mapped = res.map((horse) => ({
							value: horse.id,
							label: horse.display_name || horse.name,
						}));
						setHorseOptions(mapped);
						setHorseEmptyMessage("Type at least 3 characters");
					} else {
						setHorseOptions([]);
						setHorseEmptyMessage("No horses found");
					}
				})
				.catch((err) => {
					console.error("Error fetching horses:", err);
					setHorseOptions([]);
					setHorseEmptyMessage("Error searching horses");
				});
		}, 1000);
		return () => clearTimeout(delay);
	}, [horseInputValue]);

	const hasHorses = selectedHorses.length > 0;
	const hasFile = Boolean(pedigreeData.file);
	const canGenerateReport =
		Boolean(pedigreeData.name) &&
		Boolean(pedigreeData.type) &&
		(hasHorses || hasFile);

	return (
		<div className="component-card center premium-step2">
			<div className="flexer flexer-spaced mb-10">
				<div className="flexer flexer-vcenter">
					<div className="steps mx-10">2</div>
					<p className="input-field__label">
						Please select the horses you want to add to your list
					</p>
				</div>
				<div>
					<input
						type="file"
						accept=".csv"
						ref={fileInputRef}
						style={{ display: "none" }}
						onChange={onFileChange}
					/>
					<div className="csv-actions">
						<button
							type="button"
							onClick={onUploadCSV}
							className="button outline"
						>
							<i className="fas fa-file-excel mr-10"/>
							{fileName || "Upload CSV"}
						</button>

						<a
							href="#"
							className="underline"
							onClick={onDownloadTemplate}
						>
							Download template here
						</a>
					</div>
				</div>
			</div>

			{errorMessage && (
				<div className="alert alert__error">{errorMessage}</div>
			)}

			<div className="sire-dam-container">
				<div className="block-sire">
					<div className="select-block-row">
						<div className="select-block-col">
							<Select
								inputValue={horseInputValue}
								onInputChange={setHorseInputValue}
								options={horseOptions}
								onChange={handleHorseSelect}
								value={null}
								isClearable
								className="react-select-container"
								classNamePrefix="react-select"
								placeholder="Select a horse"
								noOptionsMessage={() => renderNoHorsesFoundMessage(horseEmptyMessage, () => setIsAddHorseModalOpen(true))}
							/>
						</div>
						<div className="option-spacing">
							{selectedHorses.map((horse, idx) => (
								<div
									key={`${horse.value}-${idx}`}
									className="selected-item"
								>
									<DisabledInputWithRemove
										value={horse.label}
										onRemove={() =>
											removeHorse(horse.value)
										}
									/>
									<div className="see-family">
										<a
											href="#"
											className="is-black"
											onClick={(e) => {
												e.preventDefault();
												openFamilyModal(horse);
											}}
										>
											<i className="far fa-eye mr-5" />
											See all family
										</a>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>

			<br />

			<div className="generate-report-button center one-fifth-width full-mobile">
				<button
					onClick={handleReportClick}
					className="button"
					disabled={!canGenerateReport}
				>
					<i className="fas fa-folder-plus mr-10" />
					{initialSelectedHorses.length
						? "Save Changes"
						: "Create List"}
				</button>
			</div>

			<FamilyModal
				isOpenModels={familyModalIsOpen}
				setIsOpenModels={setFamilyModalIsOpen}
				dataLot={selectedHorseForFamily}
			/>
			<AddHorseModal
				isOpen={isAddHorseModalOpen}
				onClose={() => setIsAddHorseModalOpen(false)}
				onResult={handleAddHorseResult}
			/>
			<SourAlert {...alertProps} />
		</div>
	);
};

export default Step2;
